import LegalPageLayout, { type LegalSection } from "@/components/feature/LegalPageLayout";

const sections: LegalSection[] = [
  {
    heading: "Agreement to These Terms",
    paragraphs: [
      "These Terms and Conditions govern your access to and use of the Rythugaru Agri Tourism website and all farm, village and rural experiences booked through it. By browsing this site, submitting an enquiry, or confirming a booking, you agree to be bound by these terms.",
      "If you do not agree with any part of these terms, please do not use our website or book an experience. These terms apply to all visitors, guests, farm partners and anyone who interacts with Rythugaru.",
    ],
  },
  {
    heading: "About Rythugaru",
    paragraphs: [
      "Rythugaru Agri Tourism connects travellers, families, students and organisations with real working farms, farmers and village communities across the Godavari delta region of Konaseema, Andhra Pradesh, India.",
      "We act as a bridge between guests and farm hosts. The farms, meals and activities you experience are delivered by independent farm families, while Rythugaru handles discovery, coordination and support.",
    ],
  },
  {
    heading: "Bookings and Reservations",
    paragraphs: [
      "All experiences are subject to availability and confirmation by the hosting farm. An enquiry submitted through our website is a request, not a confirmed booking, until you receive explicit confirmation from our team.",
      "You are responsible for providing accurate contact details, group size, dates and any accessibility or dietary requirements when making a booking. We may contact you to verify details before confirming.",
    ],
    points: [
      "Bookings are confirmed only once you receive written confirmation from Rythugaru.",
      "Shared farm and village experiences may run with other guests.",
      "Some activities are seasonal and may be substituted with a similar option.",
    ],
  },
  {
    heading: "Pricing and Payments",
    paragraphs: [
      "Prices shown for experiences are indicative and may vary based on group size, season, inclusions and travel distance. The final price will be communicated to you before confirmation.",
      "Where advance payment is required, we will share clear payment instructions. All payments are processed through secure channels, and you will receive a confirmation once payment is received.",
    ],
  },
  {
    heading: "Cancellations and Refunds",
    paragraphs: [
      "Plans change, and we try to be reasonable. Cancellations made well in advance of a scheduled experience are generally eligible for a refund or a free reschedule, minus any costs already incurred with the farm.",
      "Because farms prepare in advance, late cancellations or no-shows may not be refundable. The specific cancellation window for your experience will always be confirmed with your booking.",
    ],
  },
  {
    heading: "Guest Responsibilities and Farm Etiquette",
    paragraphs: [
      "Farms are working environments and living communities, not theme parks. By joining an experience you agree to respect the land, the animals, the farmer's home and the village around you.",
      "Guests are expected to follow the guidance of their farm host at all times, especially around machinery, water bodies, livestock and open wells.",
    ],
    points: [
      "Supervise children at all times during farm and village activities.",
      "Do not enter restricted areas, fields or animal enclosures without permission.",
      "Avoid littering, damaging crops or disturbing livestock and wildlife.",
      "Ask before photographing people, homes or cultural practices.",
    ],
  },
  {
    heading: "Farm Partner Terms",
    paragraphs: [
      "Farmers and hosts who list experiences with Rythugaru agree to provide accurate information about their farm, activities and availability, and to treat every guest with courtesy and safety.",
      "Farm partners remain responsible for the safety and legality of the activities they host, for maintaining their premises, and for any food or produce they serve or sell. Rythugaru may remove any listing that does not meet our standards.",
    ],
  },
  {
    heading: "Intellectual Property",
    paragraphs: [
      "All content on this website, including text, brand identity, photography, illustrations and layout, is owned by or licensed to Rythugaru and is protected by applicable intellectual property laws.",
      "You may not copy, reproduce, republish or use our content for commercial purposes without prior written permission, except for sharing our pages through normal social media links.",
    ],
  },
  {
    heading: "Limitation of Liability",
    paragraphs: [
      "Farm and village experiences involve outdoor and agricultural settings that carry inherent risks. While we and our hosts take reasonable care, Rythugaru is not liable for injuries, loss or damage arising from activities you choose to take part in.",
      "To the maximum extent permitted by law, our liability for any claim is limited to the amount you paid for the relevant experience. Guests are encouraged to arrange their own travel and health insurance.",
    ],
  },
  {
    heading: "Governing Law",
    paragraphs: [
      "These terms are governed by the laws of India, and any dispute arising from your use of our website or experiences will be subject to the jurisdiction of the courts of Andhra Pradesh.",
      "If any provision of these terms is found to be unenforceable, the remaining provisions will continue to apply in full.",
    ],
  },
  {
    heading: "Changes to These Terms",
    paragraphs: [
      "We may update these Terms and Conditions from time to time to reflect changes in our services, practices or legal requirements. The revised version will be posted on this page with an updated date.",
      "Your continued use of our website or services after changes are posted means you accept the updated terms.",
    ],
  },
  {
    heading: "Contact Us",
    paragraphs: [
      "If you have any questions about these Terms and Conditions, please reach out to the Rythugaru team. We are always happy to explain how things work before you book.",
      "You can contact us through the details in the footer of this website or via our contact page.",
    ],
  },
];

export default function Terms() {
  return (
    <LegalPageLayout
      eyebrow="Legal"
      title="Terms and Conditions"
      intro="Please read these terms carefully before booking or joining a Rythugaru farm experience. They explain how our experiences work, what you can expect from us, and what we ask of you as a guest."
      updated="17 September 2026"
      path="/terms"
      seoTitle="Terms and Conditions | Rythugaru Agri Tourism"
      seoDescription="Read the Rythugaru Agri Tourism Terms and Conditions: bookings, payments, cancellations, guest responsibilities and farm partner rules."
      seoImage="https://readdy.ai/api/search-image?query=Calm%20golden%20hour%20view%20of%20a%20South%20Indian%20farm%20homestead%20with%20a%20neat%20stack%20of%20ledgers%20on%20a%20wooden%20bench%2C%20coconut%20palms%20behind%2C%20warm%20amber%20light%2C%20steady%20trustworthy%20editorial%20mood%2C%20rich%20green%20and%20gold%20tones%2C%20ultra%20detailed&width=1200&height=630&seq=terms-og&orientation=landscape"
      sections={sections}
    />
  );
}